import { lazyWithPreload } from "./lazyWithPreload";

// AIDEV-NOTE: Routa kalendáře je mimo `userRouter.tsx`, aby ji mohly přednačítat i `Menu` a
// `CurrentStreak`: `userRouter` importuje Layout, ten Menu, takže import opačným směrem by tvořil cyklus.
export const calendarRoute = lazyWithPreload(async () => await import("pages/Calendar"));

export const preloadCalendarRoute = calendarRoute.preload;

/**
 * Patří chyba z `vite:preloadError` spekulativnímu přednačtení kalendáře, tedy se po ní nemá znovu načíst
 * stránka?
 *
 * AIDEV-NOTE: Rozhoduje se podle toho, KTERÝ modul selhal, ne jen podle globálního příznaku „přednačtení
 * běží“ — když během něj selže chunk jiné routy (po deployi se starým `index.html`), reload je správně a
 * student by bez něj skončil v error boundary. Událost nese `payload` (Error); jeho zpráva obsahuje adresu
 * modulu a chunk kalendáře se jmenuje `calendar-[hash].js` (`chunkFileNames` ve `vite.config.ts`; ve vývoji
 * `/src/pages/Calendar/`). Safari hlásí „Importing a module script failed.“ bez adresy: chunk pak nejde určit
 * a chyba se připíše přednačtení, které zrovna běží (jinak by se na iOS reload nepotlačil nikdy). Adresa jiného
 * souboru (`.js`/`.css`) znamená cizí chunk. Skutečná navigace na /kalendar příznak zhasne (viz
 * `lazyWithPreload`), takže její selhání reload spustí jako dřív. [TD-476]
 */
export const isSpeculativeCalendarPreloadError = (payload: unknown) => {
  if (!calendarRoute.isPreloadSpeculative()) return false;

  const message = payload instanceof Error ? payload.message : "";
  if (/\/assets\/calendar-|\/pages\/Calendar\//u.test(message)) return true;

  return !/\.(?:js|css|tsx?)\b/u.test(message);
};

// Safari `requestIdleCallback` nemá; po dvou vteřinách je aplikace po startu zpravidla klidná.
const IDLE_FALLBACK_MS = 2000;

const runWhenIdle = (callback: () => void): (() => void) => {
  if (typeof requestIdleCallback === "function") {
    const handle = requestIdleCallback(callback);

    return () => {
      cancelIdleCallback(handle);
    };
  }

  const handle = setTimeout(callback, IDLE_FALLBACK_MS);

  return () => {
    clearTimeout(handle);
  };
};

/**
 * Při startu přímo na /kalendar stáhne chunk hned (souběžně s dotazem na studenta). Jinde nedělá nic:
 * nepřihlášení, rodiče, učitelé i student v onboardingu kalendář nepotřebují, přednačtení při nečinnosti
 * spouští až `AuthenticatedArea` (`preloadCalendarRouteWhenIdle`). Selhání se tu nehlásí — chybějící chunk
 * řeší `vite:preloadError` v `index.tsx`; tohle načtení je záměrně NEspekulativní, takže reload spustí. [TD-476]
 */
export const startCalendarPreload = (pathname: string) => {
  if (!pathname.startsWith("/kalendar")) return;

  calendarRoute.preload({ speculative: false }).catch(() => undefined);
};

/**
 * Přednačte chunk kalendáře při nečinnosti prohlížeče, aby nekonkuroval kritické cestě. Vrací funkci, která
 * naplánované přednačtení zruší. Selhání se nehlásí a reload nespouští (spekulativní načtení, viz
 * `lazyWithPreload`): student o kalendář nežádal a nesmí kvůli němu přijít o rozpracované cvičení. [TD-476]
 */
export const preloadCalendarRouteWhenIdle = () =>
  runWhenIdle(() => {
    preloadCalendarRoute().catch(() => undefined);
  });
